/**
 * Hierarchy Repository - Role hierarchy visualization data access
 *
 * FERP Principles:
 * - Repository pattern: All GraphQL access through this layer
 * - Data-driven: All hierarchy data from backend
 * - Type safety: Complete TypeScript type definitions
 * - Multi-tenant: Tenant isolation enforced
 *
 * GitHub Issue: #340
 */

import { ApolloClient, gql } from '@apollo/client';
import { BaseRepository } from './BaseRepository';

// ============================================================================
// TypeScript Type Definitions
// ============================================================================

export interface RoleHierarchy {
  tenantId: string;
  roles: RoleNode[];
  relationships: RoleRelationship[];
  validationIssues: ValidationIssue[];
  rootRoleIds: string[];
  maxDepth: number;
}

export interface RoleNode {
  roleId: string;
  roleName: string;
  displayName: string;
  description: string | null;
  category: string;
  userCount: number;
  permissionCount: number;
  enabled: boolean;
  depth: number;
  parentIds: string[];
  childIds: string[];
  effectivePermissions: string[];
}

export interface RoleRelationship {
  fromRoleId: string;
  toRoleId: string;
  relationshipType: RelationType;
}

export enum RelationType {
  Inherits = 'INHERITS',
  Circular = 'CIRCULAR',
  Orphaned = 'ORPHANED',
}

export interface ValidationIssue {
  severity: IssueSeverity;
  roleId: string;
  roleName: string;
  issueType: IssueType;
  description: string;
  recommendation: string;
  autoFixAvailable: boolean;
  relatedRoleIds: string[];
}

export enum IssueSeverity {
  Critical = 'CRITICAL',
  Warning = 'WARNING',
  Info = 'INFO',
}

export enum IssueType {
  CircularDependency = 'CIRCULAR_DEPENDENCY',
  OrphanedRole = 'ORPHANED_ROLE',
  RedundantInheritance = 'REDUNDANT_INHERITANCE',
  PermissionConflict = 'PERMISSION_CONFLICT',
  DeepNesting = 'DEEP_NESTING',
}

export interface PermissionFlow {
  tenantId: string;
  permission: string;
  rolesWithPermission: PermissionFlowNode[];
}

export interface PermissionFlowNode {
  roleId: string;
  roleName: string;
  source: PermissionSource;
  sourceRoleId: string | null;
  inheritancePath: string[];
}

export enum PermissionSource {
  Direct = 'DIRECT',
  Inherited = 'INHERITED',
  Overridden = 'OVERRIDDEN',
}

export interface ValidationResult {
  valid: boolean;
  error: string | null;
}

export interface UpdateRoleParentInput {
  tenantId: string;
  roleId: string;
  newParentId: string | null;
  updatedBy: string;
}

export interface RoleDefinition {
  roleId: string;
  roleName: string;
  displayName: string;
  inheritsFrom: string[];
}

// ============================================================================
// GraphQL Operations
// ============================================================================

const GET_ROLE_HIERARCHY = gql`
  query GetRoleHierarchy($tenantId: String!) {
    getRoleHierarchy(tenantId: $tenantId) {
      tenantId
      maxDepth
      rootRoleIds
      roles {
        roleId
        roleName
        displayName
        description
        category
        userCount
        permissionCount
        enabled
        depth
        parentIds
        childIds
        effectivePermissions
      }
      relationships {
        fromRoleId
        toRoleId
        relationshipType
      }
      validationIssues {
        severity
        roleId
        roleName
        issueType
        description
        recommendation
        autoFixAvailable
        relatedRoleIds
      }
    }
  }
`;

const GET_PERMISSION_FLOW = gql`
  query GetPermissionFlow($tenantId: String!, $permission: String!) {
    getPermissionFlow(tenantId: $tenantId, permission: $permission) {
      tenantId
      permission
      rolesWithPermission {
        roleId
        roleName
        source
        sourceRoleId
        inheritancePath
      }
    }
  }
`;

const VALIDATE_ROLE_PARENT_UPDATE = gql`
  query ValidateRoleParentUpdate(
    $tenantId: String!
    $roleId: String!
    $newParentId: String!
  ) {
    validateRoleParentUpdate(
      tenantId: $tenantId
      roleId: $roleId
      newParentId: $newParentId
    ) {
      valid
      error
    }
  }
`;

const UPDATE_ROLE_PARENT = gql`
  mutation UpdateRoleParent($input: UpdateRoleParentInput!) {
    updateRoleParent(input: $input) {
      roleId
      roleName
      displayName
      inheritsFrom
    }
  }
`;

// ============================================================================
// Repository Implementation
// ============================================================================

export class HierarchyRepository extends BaseRepository<RoleHierarchy> {
  constructor() {
    super();
  }

  /**
   * Get role hierarchy for visualization
   */
  async getHierarchy(tenantId: string): Promise<RoleHierarchy> {
    const result = await this.query<{ getRoleHierarchy: RoleHierarchy }>(
      GET_ROLE_HIERARCHY,
      { tenantId }
    );

    return result.getRoleHierarchy;
  }

  /**
   * Get permission flow (trace permission through hierarchy)
   */
  async getPermissionFlow(
    tenantId: string,
    permission: string
  ): Promise<PermissionFlow> {
    const result = await this.query<{ getPermissionFlow: PermissionFlow }>(
      GET_PERMISSION_FLOW,
      { tenantId, permission }
    );

    return result.getPermissionFlow;
  }

  /**
   * Validate role parent update (check for circular dependency)
   */
  async validateParentUpdate(
    tenantId: string,
    roleId: string,
    newParentId: string
  ): Promise<ValidationResult> {
    const result = await this.query<{
      validateRoleParentUpdate: ValidationResult;
    }>(VALIDATE_ROLE_PARENT_UPDATE, { tenantId, roleId, newParentId });

    return result.validateRoleParentUpdate;
  }

  /**
   * Update role parent (drag-and-drop reorganization)
   */
  async updateParent(input: UpdateRoleParentInput): Promise<RoleDefinition> {
    const result = await this.query<{ updateRoleParent: RoleDefinition }>(
      UPDATE_ROLE_PARENT,
      { input }
    );

    if (!result.updateRoleParent) {
      throw new Error('Failed to update role parent');
    }

    return result.updateRoleParent;
  }
}

/**
 * Create HierarchyRepository instance
 */
export function createHierarchyRepository(): HierarchyRepository {
  return new HierarchyRepository();
}
